import {
    useImperativeHandle,
    type ReactElement,
    type ReactNode,
    useCallback,
    forwardRef,
    useEffect,
    Fragment,
    useState,
    useMemo,
    type Ref,
    type FC,
    useRef
} from "react";
import {
    type TextInputKeyPressEventData,
    TextInput as NativeTextInput,
    type NativeSyntheticEvent,
    type LayoutChangeEvent,
    type TextInput,
    Pressable,
    Animated,
    Platform,
    View
} from "react-native";
import type IComboboxProps from "./type";
import type {
    ComboboxTriggerWebPropsType,
    ComboboxKeyboardEventType,
    ComboboxSelectedItem,
    ComboboxLeavingItem,
    ComboboxSheetEntry,
    IComboboxChipProps,
    ComboboxComponent,
    ComboboxEntry,
    IComboboxRef
} from "./type";
import stylesheet, {
    getComboboxChipHeight,
    COMBOBOX_CHIP_SIZE,
    useStyles
} from "./stylesheet";
import {
    getComboboxLeavingAfterExit,
    getComboboxIsLimitReached,
    getComboboxControlElement,
    getComboboxResolvedItems,
    getComboboxRenderedItems,
    getComboboxVisibleCount,
    getComboboxLeavingItems,
    getComboboxCreateEntry,
    getComboboxFiltered,
    COMBOBOX_CREATE_KEY,
    getComboboxEntries,
    COMBOBOX_PAGE_ROWS,
    getComboboxGroups,
    getComboboxToggle,
    getComboboxValue,
    getComboboxMove,
    getComboboxKeys
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    getSlideStyle,
    usePresence
} from "../../helpers/motion";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    useOptionRowHighlights
} from "../optionRow/util";
import type {
    IBottomSheetRef
} from "../bottomSheet/type";
import type {
    FieldInputAccessibilityType,
    FieldInputRenderPropsType
} from "../fieldControl/type";
import {
    ChevronDown as ChevronDownIcon,
    ChevronUp as ChevronUpIcon,
    Plus as PlusIcon
} from "lucide-react-native";
import {
    getFieldSizeKey,
    getFieldColors,
    getFieldSize
} from "../fieldControl/stylesheet";
import {
    SELECT_SHEET_STANDALONE_GROUP_KEY
} from "../selectSheet/stylesheet";
import {
    FieldClearIcon
} from "../fieldControl/icons";
import {
    formatNumber
} from "../../helpers/locale";
import {
    warnOnce,
    uuid
} from "../../utils";
import FieldControl, {
    FieldAction
} from "../fieldControl";
import PopoverSurface from "../popoverSurface";
import AnimatedIcon from "../animatedIcon";
import SelectSheet from "../selectSheet";
import OptionRow from "../optionRow";
import Chip from "../chip";
import Text from "../text";

const ComboboxPlusIcon: NCoreUIKitIcon = ({
    customColor,
    size
}) => <PlusIcon
    color={customColor}
    size={size}
/>;

const ComboboxChip: FC<IComboboxChipProps> = ({
    isInitialAnimated,
    customTheme,
    isVisible,
    children,
    onExited,
    onLayout,
    offset
}) => {
    const {
        isReduceMotionEnabled,
        isRendered,
        progress
    } = usePresence({
        enterIntent: "listInsert",
        exitIntent: "listRemove",
        isInitialAnimated,
        customTheme,
        isVisible,
        onExited
    });

    const slideStyle = useMemo(() => getSlideStyle({
        isReduceMotionEnabled,
        progress,
        offset
    }), [
        isReduceMotionEnabled,
        progress,
        offset
    ]);

    if(!isRendered) {
        return null;
    }

    return <Animated.View
        style={[
            isVisible ? undefined : stylesheet.leavingChip,
            slideStyle
        ]}
        onLayout={onLayout}
    >
        {children}
    </Animated.View>;
};

function Combobox<T>({
    spreadBehaviour = "baseline",
    isShowHintTextIcon = false,
    selectBehaviour = "single",
    hintTextContainerStyle,
    isClearEnabled = false,
    CustomPrefixComponent,
    accessibilityLabel,
    isDisabled = false,
    isReadOnly = false,
    isRequired = false,
    isLoading = false,
    searchPlaceholder,
    subTitleExtractor,
    renderOptionIcon,
    type = "neutral",
    value: valueProp,
    customLocalize,
    groupExtractor,
    titleExtractor,
    onValueChange,
    defaultValue,
    keyExtractor,
    customTheme,
    placeholder,
    isOptional,
    emptyText,
    maxChoice,
    minChoice,
    hintText,
    onCreate,
    onSearch,
    style,
    title,
    data,
    size,
    id
}: IComboboxProps<T>, ref: Ref<IComboboxRef>) {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        durations,
        iconSizes,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        activeLocale,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const {
        getHighlightProps,
        isHighlighted
    } = useOptionRowHighlights();

    const anchorRef = useRef<View | null>(null);

    const triggerRef = useRef<View>(null);

    const searchRef = useRef<TextInput>(null);

    const inputRef = useRef<TextInput>(null);

    const bottomSheetRef = useRef<IBottomSheetRef>(null);

    const generatedID = useRef(uuid());

    const sheetKey = useRef(uuid());

    const [
        isOpen,
        setIsOpen
    ] = useState(false);

    const [
        searchText,
        setSearchText
    ] = useState("");

    const [
        isReadOnlyFocused,
        setIsReadOnlyFocused
    ] = useState(false);

    const [
        activeKey,
        setActiveKey
    ] = useState<string | undefined>(undefined);

    const isControlled = valueProp !== undefined;

    const getInitialItems = () => getComboboxResolvedItems({
        keys: getComboboxKeys({
            value: isControlled ? valueProp : defaultValue,
            isMultiple: selectBehaviour === "multiple"
        }),
        entries: getComboboxEntries({
            subTitleExtractor,
            groupExtractor,
            titleExtractor,
            keyExtractor,
            items: data
        }),
        known: new Map(),
        current: []
    });

    const [
        selectedItems,
        setSelectedItems
    ] = useState<Array<ComboboxSelectedItem>>(getInitialItems);

    const [
        pendingItems,
        setPendingItems
    ] = useState<Array<ComboboxSelectedItem>>(getInitialItems);

    const [
        remote,
        setRemote
    ] = useState<{ text: string; items: Array<T> } | undefined>(undefined);

    const [
        plainWidths,
        setPlainWidths
    ] = useState<Record<string, number>>({});

    const [
        leavingItems,
        setLeavingItems
    ] = useState<Array<ComboboxLeavingItem>>([]);

    const [
        closeExtra,
        setCloseExtra
    ] = useState(0);

    const [
        plusWidth,
        setPlusWidth
    ] = useState(0);

    const [
        chipsWidth,
        setChipsWidth
    ] = useState(0);

    const selectedRef = useRef(selectedItems);

    const knownRef = useRef<Map<string, string>>(new Map());

    const pendingRef = useRef(pendingItems);

    const enteringKeysRef = useRef<Set<string>>(new Set());

    const onSearchRef = useRef(onSearch);

    onSearchRef.current = onSearch;

    const inputID = id ?? generatedID.current;

    const valueID = `${inputID}-value`;

    const listboxID = `${inputID}-listbox`;

    const isMultiple = selectBehaviour === "multiple";

    const isCoarse = pointerType === "coarse";

    const isInteractive = !isDisabled && !isReadOnly;

    const isRemote = !!onSearch;

    const query = searchText.trim();

    const isInline = isInteractive && !isCoarse;

    const isPopoverOpen = isOpen && !isCoarse;

    const isSheetActive = isOpen && isCoarse;

    const currentSizeKey = getFieldSizeKey({
        isActionable: isMultiple,
        pointerType,
        size
    });

    const currentSize = getFieldSize({
        size: currentSizeKey,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        spaces
    });

    const currentColors = getFieldColors({
        isDisabled,
        isReadOnly,
        colors,
        type
    });

    const chipHeight = getComboboxChipHeight({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes
    });

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState({
        isDisabled: !isInteractive
    });

    const {
        searchInput: searchInputDynamicStyle,
        chevron: chevronDynamicStyle,
        measure: measureDynamicStyle,
        trigger: triggerDynamicStyle,
        anchor: anchorDynamicStyle,
        chips: chipsDynamicStyle,
        live: liveDynamicStyle
    } = useStyles({
        currentSize,
        chipHeight,
        isMultiple,
        isLoading,
        borders,
        spaces
    });

    const baseEntries = useMemo(() => getComboboxEntries({
        subTitleExtractor,
        groupExtractor,
        titleExtractor,
        keyExtractor,
        items: data
    }), [
        subTitleExtractor,
        groupExtractor,
        titleExtractor,
        keyExtractor,
        data
    ]);

    const isPending = isRemote && query.length > 0 && remote?.text !== query;

    const remoteItems = isRemote && query.length && remote ? remote.items : undefined;

    const sourceEntries = useMemo(() => remoteItems ? getComboboxEntries({
        items: remoteItems,
        subTitleExtractor,
        groupExtractor,
        titleExtractor,
        keyExtractor
    }) : baseEntries, [
        subTitleExtractor,
        groupExtractor,
        titleExtractor,
        keyExtractor,
        remoteItems,
        baseEntries
    ]);

    const filteredEntries = useMemo(() => getComboboxFiltered({
        isLocalFilter: !isRemote,
        entries: sourceEntries,
        locale: activeLocale,
        query
    }), [
        sourceEntries,
        activeLocale,
        isRemote,
        query
    ]);

    const createEntry = isPending ? undefined : getComboboxCreateEntry({
        createLabel: localize("combobox-add-item", [
            query
        ]),
        entries: filteredEntries,
        locale: activeLocale,
        onCreate,
        query
    });

    const options: Array<ComboboxEntry<T>> = isPending ? [] : [
        ...filteredEntries,
        ...createEntry ? [
            createEntry
        ] : []
    ];

    const selectedKeys = new Set(selectedItems.map((item) => item.__key));

    const optionIndexes = new Map(options.map((option, index) => [
        option.key,
        index
    ]));

    const getIsOptionDisabled = (option: ComboboxEntry<T>) => {
        if(option.isCreate) {
            return false;
        }

        return getComboboxIsLimitReached({
            isSelected: selectedKeys.has(option.key),
            selectedCount: selectedItems.length,
            isMultiple,
            maxChoice
        });
    };

    const enabledKeys = options.filter((option) => !getIsOptionDisabled(option)).map((option) => option.key);

    const optionsSignature = options.map((option) => option.key).join("|");

    const getOptionID = (key?: string) => {
        const index = key === undefined ? undefined : optionIndexes.get(key);

        return index === undefined ? undefined : `${listboxID}-${index}`;
    };

    const chipWidthItems = selectedItems.map((item) => {
        const plainWidth = plainWidths[item.__key];

        return plainWidth === undefined ? undefined : plainWidth + closeExtra;
    });

    const visibleCount = getComboboxVisibleCount({
        widths: chipWidthItems,
        available: chipsWidth,
        gap: spaces.spacingXs,
        plusWidth
    });

    const renderedItems = getComboboxRenderedItems({
        selectedItems,
        leavingItems,
        visibleCount
    });

    useImperativeHandle(
        ref,
        () => ({
            focus: () => isInline ? inputRef.current?.focus() : triggerRef.current?.focus(),
            clearSelections: () => handleCommit([]),
            close: () => handleClose(),
            open: () => handleOpen()
        })
    );

    useEffect(() => {
        enteringKeysRef.current.clear();
    });

    useEffect(() => {
        if(!isControlled || onValueChange) {
            return;
        }

        warnOnce({
            message: "Combobox: a controlled \"value\" needs an \"onValueChange\" handler that updates it."
        });
    }, [
        onValueChange,
        isControlled
    ]);

    const controlledSignature = isControlled ? getComboboxKeys({
        value: valueProp,
        isMultiple
    }).join("|") : undefined;

    useEffect(() => {
        if(!isControlled) {
            return;
        }

        const nextItems = getComboboxResolvedItems({
            current: selectedRef.current,
            known: knownRef.current,
            keys: getComboboxKeys({
                value: valueProp,
                isMultiple
            }),
            entries: baseEntries
        });

        const getSignature = (items: Array<ComboboxSelectedItem>) => items.map((item) => `${item.__key}:${item.__title}`).join("|");

        if(getSignature(nextItems) !== getSignature(selectedRef.current)) {
            updateSelectedItems(nextItems);
        }
    }, [
        controlledSignature,
        baseEntries,
        isControlled
    ]);

    useEffect(() => {
        if(!isRemote || !isOpen || !query.length || remote?.text === query) {
            return;
        }

        let isCancelled = false;

        const timer = setTimeout(() => {
            const search = onSearchRef.current;

            if(!search) {
                return;
            }

            search(query)
                .catch((): Array<T> => [])
                .then((items) => {
                    if(isCancelled) {
                        return;
                    }

                    setRemote({
                        text: query,
                        items
                    });
                });
        }, durations.medium);

        return () => {
            isCancelled = true;

            clearTimeout(timer);
        };
    }, [
        durations.medium,
        isRemote,
        isOpen,
        query
    ]);

    useEffect(() => {
        if(!isPopoverOpen) {
            return;
        }

        const selectedKey = enabledKeys.find((key) => selectedKeys.has(key));

        setActiveKey(query.length ? enabledKeys[0] : selectedKey ?? enabledKeys[0]);
    }, [
        optionsSignature,
        isPopoverOpen,
        query
    ]);

    useEffect(() => {
        const elementID = getOptionID(activeKey);

        if(Platform.OS !== "web" || !isPopoverOpen || !elementID || typeof globalThis.document === "undefined") {
            return;
        }

        globalThis.document.getElementById(elementID)?.scrollIntoView({
            block: "nearest"
        });
    }, [
        isPopoverOpen,
        activeKey
    ]);

    const updateSelectedItems = (nextItems: Array<ComboboxSelectedItem>) => {
        const previousKeys = new Set(selectedRef.current.map((item) => item.__key));

        nextItems.forEach((item) => {
            if(!previousKeys.has(item.__key)) {
                enteringKeysRef.current.add(item.__key);
            }
        });

        if(isMultiple) {
            setLeavingItems((previousItems) => getComboboxLeavingItems({
                leavingItems: previousItems,
                renderedItems,
                nextItems
            }));
        }

        selectedRef.current = nextItems;

        setSelectedItems(nextItems);
    };

    const handleLeavingExit = (key: string) => {
        setLeavingItems((previousItems) => getComboboxLeavingAfterExit({
            leavingItems: previousItems,
            key
        }));
    };

    const updatePendingItems = (nextItems: Array<ComboboxSelectedItem>) => {
        pendingRef.current = nextItems;

        setPendingItems(nextItems);
    };

    const handleOpen = (seed = "") => {
        if(!isInteractive) {
            return;
        }

        setSearchText(seed);

        if(isMultiple) {
            updatePendingItems(selectedRef.current);
        }

        setIsOpen(true);
    };

    const handleClose = () => {
        setIsOpen(false);

        setSearchText("");

        setActiveKey(undefined);
    };

    const handleSheetClose = () => {
        bottomSheetRef.current?.close(() => {
            handleClose();
        });
    };

    const handleToggleOpen = () => {
        if(isOpen) {
            if(isCoarse) {
                handleSheetClose();

                return;
            }

            handleClose();

            return;
        }

        handleOpen();
    };

    const handleCommit = (nextItems: Array<ComboboxSelectedItem>) => {
        nextItems.forEach((item) => knownRef.current.set(item.__key, item.__title));

        if(!isControlled) {
            updateSelectedItems(nextItems);
        }

        onValueChange?.(getComboboxValue({
            items: nextItems,
            isMultiple
        }), nextItems);
    };

    const handleToggleItem = (item: ComboboxSelectedItem, current: Array<ComboboxSelectedItem>) => {
        return getComboboxToggle({
            isMultiple,
            maxChoice,
            minChoice,
            current,
            item
        });
    };

    const handleSelectOption = (option: ComboboxEntry<T>) => {
        if(option.isCreate) {
            onCreate?.(query);

            handleClose();

            return;
        }

        const result = handleToggleItem({
            __title: option.title,
            __key: option.key
        }, selectedRef.current);

        if(result.isChanged) {
            handleCommit(result.items);
        }

        if(!isMultiple) {
            handleClose();

            return;
        }

        if(isInline) {
            setSearchText("");
        }
    };

    const handleClearPress = () => {
        if(selectedRef.current.length) {
            handleCommit([]);
        }

        if(isInline) {
            setSearchText("");

            inputRef.current?.focus();

            return;
        }

        triggerRef.current?.focus();
    };

    const handleRemoveItem = (item: ComboboxSelectedItem) => {
        const result = handleToggleItem(item, selectedRef.current);

        if(result.isChanged) {
            handleCommit(result.items);
        }
    };

    const handleSheetSelect = (item: ComboboxSelectedItem) => {
        if(item.__key === COMBOBOX_CREATE_KEY) {
            onCreate?.(query);

            handleSheetClose();

            return;
        }

        if(isMultiple) {
            updatePendingItems(handleToggleItem(item, pendingRef.current).items);

            return;
        }

        const result = handleToggleItem(item, selectedRef.current);

        if(result.isChanged) {
            handleCommit(result.items);
        }

        handleSheetClose();
    };

    const handleSheetOk = () => {
        handleCommit(pendingRef.current);

        handleSheetClose();
    };

    const handleSheetSelectAll = () => {
        const limit = maxChoice !== undefined && maxChoice > 0 ? maxChoice : baseEntries.length;

        updatePendingItems(baseEntries.slice(0, limit).map((entry) => ({
            __title: entry.title,
            __key: entry.key
        })));
    };

    const handleSheetClear = () => {
        updatePendingItems([]);
    };

    const handleMoveActive = (delta: number) => {
        setActiveKey(getComboboxMove({
            keys: enabledKeys,
            activeKey,
            delta
        }));
    };

    const handleActivateCurrent = () => {
        const option = options.find((item) => item.key === activeKey);

        if(option) {
            handleSelectOption(option);
        }
    };

    const handleRemoveLast = () => {
        const last = selectedRef.current[selectedRef.current.length - 1];

        if(last) {
            handleRemoveItem(last);
        }
    };

    const onSearchKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        const key = event.nativeEvent.key;

        if(isInline && !isOpen && (key === "ArrowDown" || key === "ArrowUp" || key === "Enter")) {
            event.preventDefault();

            handleOpen();

            return;
        }

        if(key === "ArrowDown" || key === "ArrowUp") {
            event.preventDefault();

            handleMoveActive(key === "ArrowDown" ? 1 : -1);

            return;
        }

        if(key === "PageDown" || key === "PageUp") {
            event.preventDefault();

            handleMoveActive(key === "PageDown" ? COMBOBOX_PAGE_ROWS : -COMBOBOX_PAGE_ROWS);

            return;
        }

        if(key === "Enter") {
            event.preventDefault();

            handleActivateCurrent();

            return;
        }

        if(key === "Escape" && isInline && isOpen) {
            event.preventDefault();

            handleClose();

            return;
        }

        if(key === "Tab") {
            handleClose();

            return;
        }

        if(key === "Backspace" && isMultiple && !searchText.length) {
            handleRemoveLast();
        }
    };

    const handleChangeText = (text: string) => {
        if(isOpen) {
            setSearchText(text);

            return;
        }

        handleOpen(text);
    };

    const handleFocusRequest = () => {
        inputRef.current?.focus();

        if(!isOpen) {
            handleOpen();
        }
    };

    const onTriggerKeyDown = (event: ComboboxKeyboardEventType) => {
        if(!isInteractive || event.altKey || event.ctrlKey || event.metaKey) {
            return;
        }

        if(event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();

            handleOpen();

            return;
        }

        if(event.key && event.key.length === 1 && event.key !== " " && !isOpen) {
            event.preventDefault();

            handleOpen(event.key);
        }
    };

    const onChipsLayout = (event: LayoutChangeEvent) => {
        setChipsWidth(event.nativeEvent.layout.width - (isInline ? currentSize.minHeight * 2 : 0));
    };

    const onPlusLayout = (event: LayoutChangeEvent) => {
        setPlusWidth(event.nativeEvent.layout.width);
    };

    const onPlainChipLayout = (key: string, event: LayoutChangeEvent) => {
        const width = event.nativeEvent.layout.width;

        setPlainWidths((previousWidths) => {
            if(previousWidths[key] === width) {
                return previousWidths;
            }

            return {
                ...previousWidths,
                [key]: width
            };
        });
    };

    const onRealChipLayout = (key: string, event: LayoutChangeEvent) => {
        const plainWidth = plainWidths[key];

        if(plainWidth === undefined || !isInteractive) {
            return;
        }

        setCloseExtra(Math.max(0, event.nativeEvent.layout.width - plainWidth));
    };

    const getDisplayText = () => {
        if(!selectedItems.length) {
            return undefined;
        }

        return selectedItems.map((item) => item.__title).join(", ");
    };

    const getPlaceholder = () => {
        return placeholder ?? localize("select-an-option");
    };

    const getSummaryText = () => {
        if(!selectedItems.length) {
            return getPlaceholder();
        }

        return localize("combobox-selected-summary", [
            formatNumber({
                value: selectedItems.length,
                locale: activeLocale
            }),
            getDisplayText() ?? ""
        ]);
    };

    const getSubTitle = () => {
        if(isOptional && !isRequired) {
            return localize("is-optional");
        }

        return undefined;
    };

    const fieldLabel = title || accessibilityLabel;

    const getUntitledLabel = () => {
        const valueText = isMultiple ? getSummaryText() : getDisplayText() ?? getPlaceholder();

        return accessibilityLabel ? `${accessibilityLabel}, ${valueText}` : valueText;
    };

    const getTriggerAccessibilityProps = (accessibilityProps: FieldInputAccessibilityType) => {
        if(Platform.OS === "web") {
            const webProps: ComboboxTriggerWebPropsType = {
                "aria-controls": isPopoverOpen ? listboxID : undefined,
                "aria-haspopup": "listbox",
                onKeyDown: onTriggerKeyDown
            };

            if(isMultiple) {
                return {
                    ...accessibilityProps,
                    ...webProps,
                    "aria-label": fieldLabel ? `${fieldLabel}, ${getSummaryText()}` : getSummaryText(),
                    "aria-labelledby": undefined
                };
            }

            if(!title && accessibilityLabel) {
                return {
                    ...accessibilityProps,
                    ...webProps,
                    "aria-label": `${accessibilityLabel}, ${getDisplayText() ?? getPlaceholder()}`,
                    "aria-labelledby": undefined
                };
            }

            return {
                ...accessibilityProps,
                ...webProps,
                "aria-labelledby": accessibilityProps["aria-labelledby"] ? `${accessibilityProps["aria-labelledby"]} ${valueID}` : valueID
            };
        }

        if(!title) {
            return {
                ...accessibilityProps,
                "aria-label": getUntitledLabel()
            };
        }

        if(Platform.OS !== "ios") {
            return accessibilityProps;
        }

        return {
            ...accessibilityProps,
            accessibilityValue: {
                text: getSummaryText()
            }
        };
    };

    const renderPlainChip = (item: ComboboxSelectedItem) => {
        return <View
            onLayout={(event) => onPlainChipLayout(item.__key, event)}
            style={stylesheet.measureItem}
            key={item.__key}
        >
            <Chip
                customTheme={customTheme}
                size={COMBOBOX_CHIP_SIZE}
                title={item.__title}
                isClosable={false}
                type="neutral"
            />
        </View>;
    };

    const renderMeasure = () => {
        return <View
            style={[
                stylesheet.measure,
                measureDynamicStyle
            ]}
            aria-hidden={true}
        >
            {selectedItems.map((item) => renderPlainChip(item))}
            <View
                style={stylesheet.measureItem}
                onLayout={onPlusLayout}
            >
                <Chip
                    title={`+${formatNumber({
                        value: selectedItems.length,
                        locale: activeLocale
                    })}`}
                    titleStyle={stylesheet.tabular}
                    customTheme={customTheme}
                    size={COMBOBOX_CHIP_SIZE}
                    isClosable={false}
                    type="neutral"
                />
            </View>
        </View>;
    };

    const renderChips = (placeholderTextColor: string) => {
        if(!selectedItems.length && !leavingItems.length) {
            if(isInline) {
                return null;
            }

            return <Text
                variant={currentSize.valueVariant}
                customColor={placeholderTextColor}
                customTheme={customTheme}
                style={stylesheet.value}
                numberOfLines={1}
                id={valueID}
            >
                {getPlaceholder()}
            </Text>;
        }

        const hiddenCount = selectedItems.length - visibleCount;

        return <View
            style={[
                stylesheet.chips,
                chipsDynamicStyle,
                isInline ? stylesheet.inlineChips : undefined
            ]}
            onLayout={isInline ? undefined : onChipsLayout}
        >
            {
                renderedItems.map(({
                    isLeaving,
                    item
                }) => <ComboboxChip
                    isInitialAnimated={enteringKeysRef.current.has(item.__key)}
                    onLayout={(event) => onRealChipLayout(item.__key, event)}
                    onExited={() => handleLeavingExit(item.__key)}
                    offset={spaces.spacingSm}
                    customTheme={customTheme}
                    isVisible={!isLeaving}
                    key={item.__key}
                >
                    <Chip
                        onClose={isInteractive ? () => handleRemoveItem(item) : undefined}
                        isClosable={isInteractive}
                        customTheme={customTheme}
                        size={COMBOBOX_CHIP_SIZE}
                        isDisabled={isDisabled}
                        title={item.__title}
                        type="neutral"
                    />
                </ComboboxChip>)
            }
            {
                hiddenCount > 0 ?
                    <View
                        aria-hidden={true}
                    >
                        <Chip
                            titleStyle={stylesheet.tabular}
                            title={`+${formatNumber({
                                locale: activeLocale,
                                value: hiddenCount
                            })}`}
                            customTheme={customTheme}
                            size={COMBOBOX_CHIP_SIZE}
                            isDisabled={isDisabled}
                            isClosable={false}
                            type="neutral"
                        />
                    </View>
                :
                    null
            }
        </View>;
    };

    const getSearchInputLabel = () => {
        const displayText = getDisplayText();

        if(!displayText) {
            return title ? undefined : accessibilityLabel ?? getPlaceholder();
        }

        const valueText = isMultiple ? getSummaryText() : displayText;

        return fieldLabel ? `${fieldLabel}, ${valueText}` : valueText;
    };

    const renderSearchInput = ({
        placeholderTextColor,
        accessibilityProps,
        style: inputStyle
    }: FieldInputRenderPropsType) => {
        const displayText = getDisplayText();

        const isValuePlaceholder = !isMultiple && !!displayText && !isOpen;

        return <NativeTextInput
            {...accessibilityProps}
            placeholder={isMultiple ? selectedItems.length ? undefined : getPlaceholder() : displayText ?? getPlaceholder()}
            placeholderTextColor={isValuePlaceholder ? currentColors.valueColor : placeholderTextColor}
            aria-labelledby={getDisplayText() ? undefined : accessibilityProps["aria-labelledby"]}
            aria-activedescendant={isPopoverOpen ? getOptionID(activeKey) : undefined}
            aria-controls={isPopoverOpen ? listboxID : undefined}
            onFocus={interactionHandlers.onFocus}
            underlineColorAndroid="transparent"
            onBlur={interactionHandlers.onBlur}
            aria-label={getSearchInputLabel()}
            onChangeText={handleChangeText}
            onKeyPress={onSearchKeyPress}
            style={[
                stylesheet.searchInput,
                inputStyle,
                searchInputDynamicStyle
            ]}
            aria-autocomplete="list"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            autoCorrect={false}
            value={searchText}
            multiline={false}
            role="combobox"
            ref={inputRef}
        />;
    };

    const renderValue = (placeholderTextColor: string) => {
        if(isMultiple) {
            return renderChips(placeholderTextColor);
        }

        const displayText = getDisplayText();

        return <Text
            customColor={displayText ? currentColors.valueColor : placeholderTextColor}
            variant={currentSize.valueVariant}
            customTheme={customTheme}
            style={stylesheet.value}
            numberOfLines={1}
            id={valueID}
        >
            {displayText ?? getPlaceholder()}
        </Text>;
    };

    const renderClearAction = () => {
        if(!isClearEnabled || !isInteractive || isLoading || isMultiple && minChoice) {
            return null;
        }

        return <FieldAction
            accessibilityLabel={localize("clear-selection")}
            isVisible={selectedItems.length > 0}
            currentColors={currentColors}
            onPress={handleClearPress}
            currentSize={currentSize}
            customTheme={customTheme}
            icon={FieldClearIcon}
            isMultiline={false}
            isDisabled={false}
        />;
    };

    const renderChevron = () => {
        if(isLoading || isReadOnly) {
            return null;
        }

        return <View
            style={[
                stylesheet.chevron,
                chevronDynamicStyle
            ]}
            aria-hidden={true}
        >
            <AnimatedIcon
                customColor={currentColors.iconColor}
                size={currentSize.iconSize}
                fromIcon={ChevronDownIcon}
                isTransitioned={isOpen}
                toIcon={ChevronUpIcon}
                iconTransition="morph"
            />
        </View>;
    };

    const onReadOnlyFocus = () => {
        setIsReadOnlyFocused(true);
    };

    const onReadOnlyBlur = () => {
        setIsReadOnlyFocused(false);
    };

    const handleAnchorRef = useCallback((node: View | null) => {
        anchorRef.current = getComboboxControlElement({
            node
        });
    }, []);

    const renderTrigger = ({
        style: inputDynamicStyle,
        placeholderTextColor,
        accessibilityProps
    }: FieldInputRenderPropsType) => {
        if(isInline) {
            const searchInput = renderSearchInput({
                style: inputDynamicStyle,
                placeholderTextColor,
                accessibilityProps
            });

            return <View
                style={[
                    stylesheet.anchor,
                    anchorDynamicStyle
                ]}
                ref={handleAnchorRef}
            >
                <View
                    style={stylesheet.content}
                >
                    {CustomPrefixComponent ? CustomPrefixComponent() : null}
                    {
                        isMultiple ?
                            <View
                                onLayout={onChipsLayout}
                                style={[
                                    stylesheet.chips,
                                    chipsDynamicStyle
                                ]}
                            >
                                {renderChips(placeholderTextColor)}
                                {searchInput}
                            </View>
                        :
                            searchInput
                    }
                </View>
                {renderClearAction()}
                {renderChevron()}
                {isMultiple ? renderMeasure() : null}
            </View>;
        }

        if(isReadOnly) {
            return <NativeTextInput
                {...accessibilityProps}
                value={isMultiple ? getDisplayText() ?? "" : selectedItems[0]?.__title ?? ""}
                placeholderTextColor={placeholderTextColor}
                underlineColorAndroid="transparent"
                placeholder={getPlaceholder()}
                style={[
                    stylesheet.readOnlyInput,
                    inputDynamicStyle
                ]}
                onFocus={onReadOnlyFocus}
                onBlur={onReadOnlyBlur}
                multiline={false}
                editable={false}
            />;
        }

        return <View
            style={[
                stylesheet.anchor,
                anchorDynamicStyle
            ]}
            ref={handleAnchorRef}
        >
            <Pressable
                {...getTriggerAccessibilityProps(accessibilityProps)}
                {...interactionHandlers}
                aria-busy={isLoading || undefined}
                onPress={handleToggleOpen}
                style={[
                    stylesheet.trigger,
                    triggerDynamicStyle
                ]}
                aria-expanded={isOpen}
                disabled={isDisabled}
                ref={triggerRef}
                role="button"
            />
            <View
                style={stylesheet.content}
            >
                {CustomPrefixComponent ? CustomPrefixComponent() : null}
                {renderValue(placeholderTextColor)}
            </View>
            {renderClearAction()}
            {renderChevron()}
            {isMultiple ? renderMeasure() : null}
        </View>;
    };

    const renderOptionLabel = (option: ComboboxEntry<T>): ReactNode => {
        const match = option.match;

        if(!match || option.isCreate) {
            return option.title;
        }

        return <Fragment>
            {option.title.slice(0, match.start)}
            <OptionRow.Highlight
                customTheme={customTheme}
            >
                {option.title.slice(match.start, match.end)}
            </OptionRow.Highlight>
            {option.title.slice(match.end)}
        </Fragment>;
    };

    const renderOption = (option: ComboboxEntry<T>) => {
        const isDisabledOption = getIsOptionDisabled(option);

        const optionIcon = option.item !== undefined && renderOptionIcon ? renderOptionIcon(option.item) : undefined;

        return <OptionRow
            {...getHighlightProps(option.key)}
            meta={isDisabledOption ? localize("combobox-limit-reached") : option.sub}
            onHoverIn={isDisabledOption ? undefined : () => setActiveKey(option.key)}
            isHoverSuppressed={activeKey !== undefined && option.key !== activeKey}
            icon={option.isCreate ? ComboboxPlusIcon : undefined}
            onPress={() => handleSelectOption(option)}
            isSelected={selectedKeys.has(option.key)}
            isActive={option.key === activeKey}
            label={renderOptionLabel(option)}
            isDisabled={isDisabledOption}
            id={getOptionID(option.key)}
            customTheme={customTheme}
            isShowActiveRing={false}
            isMultiple={isMultiple}
            leading={optionIcon}
            key={option.key}
        />;
    };

    const renderGroups = () => {
        const groups = getComboboxGroups({
            entries: options
        });

        return groups.map((group, groupIndex) => {
            const previousGroup = groups[groupIndex - 1];

            const previousKey = previousGroup?.options[previousGroup.options.length - 1]?.key;

            const nextKey = group.options[0]?.key;

            const rows = group.options.map((option) => renderOption(option));

            return <Fragment
                key={`${group.heading ?? ""}-${nextKey}`}
            >
                {
                    previousGroup ?
                        <PopoverSurface.Separator
                            isHighlighted={isHighlighted(previousKey) || isHighlighted(nextKey)}
                            customTheme={customTheme}
                        />
                    :
                        null
                }
                {
                    group.heading ?
                        <PopoverSurface.Group
                            customTheme={customTheme}
                            heading={group.heading}
                        >
                            {rows}
                        </PopoverSurface.Group>
                    :
                        rows
                }
            </Fragment>;
        });
    };

    const renderFeedback = () => {
        if(isPending || isLoading) {
            return <PopoverSurface.Status
                customLocalize={customLocalize}
                customTheme={customTheme}
            >
                {isPending ? localize("combobox-searching") : localize("loading")}
            </PopoverSurface.Status>;
        }

        if(options.length) {
            return null;
        }

        return <PopoverSurface.Empty
            searchText={query.length ? query : undefined}
            customLocalize={customLocalize}
            customTheme={customTheme}
            title={emptyText}
        />;
    };

    const renderLiveRegion = () => {
        if(isPending || !query.length) {
            return null;
        }

        return <Text
            customTheme={customTheme}
            style={[
                stylesheet.live,
                liveDynamicStyle
            ]}
            aria-live="polite"
            role="status"
        >
            {localize("combobox-results-count", [
                formatNumber({
                    value: options.length - (createEntry ? 1 : 0),
                    locale: activeLocale
                })
            ])}
        </Text>;
    };

    const renderPopover = (): ReactElement | null => {
        if(isCoarse) {
            return null;
        }

        return <PopoverSurface
            initialFocusRef={isInline ? undefined : searchRef}
            returnFocusRef={isInline ? inputRef : triggerRef}
            customTheme={customTheme}
            isAutoFocused={!isInline}
            onDismiss={handleClose}
            isOpen={isPopoverOpen}
            anchorRef={anchorRef}
            minWidth="anchor"
            width="anchor"
        >
            {
                isInline ?
                    null
                :
                    <PopoverSurface.Search
                        placeholder={searchPlaceholder ?? localize("search")}
                        aria-label={searchPlaceholder ?? localize("search")}
                        aria-activedescendant={getOptionID(activeKey)}
                        onClear={() => setSearchText("")}
                        customLocalize={customLocalize}
                        onKeyPress={onSearchKeyPress}
                        onChangeText={setSearchText}
                        customTheme={customTheme}
                        aria-controls={listboxID}
                        aria-expanded={true}
                        value={searchText}
                        ref={searchRef}
                    />
            }
            <PopoverSurface.List
                customTheme={customTheme}
                isMultiple={isMultiple}
                aria-label={title}
                isBusy={isPending}
                id={listboxID}
            >
                {renderGroups()}
            </PopoverSurface.List>
            {renderFeedback()}
            {renderLiveRegion()}
        </PopoverSurface>;
    };

    const getSheetEntry = (entry: ComboboxEntry<T>): ComboboxSheetEntry => {
        return {
            group: entry.isCreate ? SELECT_SHEET_STANDALONE_GROUP_KEY : entry.group ?? "",
            __title: entry.title,
            __key: entry.key
        };
    };

    const renderSheetOptionIcon = ({
        item
    }: {
        item: ComboboxSelectedItem;
    }): ReactElement => {
        if(item.__key === COMBOBOX_CREATE_KEY) {
            return <ComboboxPlusIcon
                customColor={colors.content.icon.high}
                size={iconSizes.small}
                color="high"
            />;
        }

        const entry = [
            ...baseEntries,
            ...options
        ].find((candidate) => candidate.key === item.__key);

        if(!renderOptionIcon || entry?.item === undefined) {
            return <Fragment />;
        }

        return renderOptionIcon(entry.item);
    };

    const renderSheet = (): ReactElement | null => {
        if(!isCoarse) {
            return null;
        }

        return <SelectSheet
            groupExtractor={groupExtractor ? (item) => "group" in item ? item.group : "" : undefined}
            mainSelectedItems={isMultiple ? pendingItems : selectedItems}
            searchedData={options.map(getSheetEntry)}
            renderOptionIcon={renderSheetOptionIcon}
            data={baseEntries.map(getSheetEntry)}
            keyExtractor={(item) => item.__key}
            isLoading={isPending || isLoading}
            setIsMoreLoading={() => undefined}
            isWorkWithRealtime={!isMultiple}
            selectObject={handleSheetSelect}
            selectAll={handleSheetSelectAll}
            customLocalize={customLocalize}
            selectBoxKey={sheetKey.current}
            bottomSheetRef={bottomSheetRef}
            setIsLoading={() => undefined}
            setSearchText={setSearchText}
            isMultipleSelect={isMultiple}
            setIsActive={(isActive) => {
                if(!isActive) {
                    handleClose();
                }
            }}
            maxChoice={maxChoice ?? -1}
            clearAll={handleSheetClear}
            customTheme={customTheme}
            cancel={handleSheetClose}
            searchText={searchText}
            isOpen={isSheetActive}
            isMoreLoading={false}
            minChoice={minChoice}
            isShowTools={false}
            isSearchable={true}
            ok={handleSheetOk}
            title={title}
        />;
    };

    return <Fragment>
        <FieldControl
            onFocusRequest={isInteractive ? isInline ? handleFocusRequest : handleToggleOpen : undefined}
            isFocused={isOpen || isFocusVisible || isReadOnlyFocused}
            hintTextContainerStyle={hintTextContainerStyle}
            accessibilityLabel={accessibilityLabel}
            isShowHintTextIcon={isShowHintTextIcon}
            spreadBehaviour={spreadBehaviour}
            customLocalize={customLocalize}
            renderInput={renderTrigger}
            customTheme={customTheme}
            subTitle={getSubTitle()}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            isRequired={isRequired}
            isLoading={isLoading}
            size={currentSizeKey}
            hintText={hintText}
            inputID={inputID}
            title={title}
            style={style}
            type={type}
        />
        {renderPopover()}
        {renderSheet()}
    </Fragment>;
};
export default forwardRef(Combobox) as unknown as ComboboxComponent;
